import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import ReviewFormInput from '../reviews/ReviewFormInput';
import {
  Card,
  Button,
  Input,
  Select,
  Toggle,
  RichTextEditor,
  Spinner
} from '../../components/common';
import { careerService } from '../../services/careerService';
import { useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import './CareerFormPage.css';

const typeOptions = [
  { value: 'full-time',  label: 'Full-time' },
  { value: 'part-time',  label: 'Part-time' },
  { value: 'contract',   label: 'Contract' },
  { value: 'internship', label: 'Internship' }
];

const CareerFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const validationRules = {
    title:      [validators.required, validators.minLength(3), validators.maxLength(200)],
    department: [validators.required, validators.maxLength(100)],
    location:   [validators.required, validators.maxLength(150)],
    experience: [validators.required, validators.maxLength(50)],
    type:       [validators.required]
  };

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    validate
  } = useForm(
    {
      title:        '',
      department:   '',
      location:     '',
      type:         'full-time',
      experience:   '',
      description:  '',
      requirements: '',
      status:       'open'
    },
    validationRules
  );

  const fetchCareer = useCallback(async () => {
    try {
      const response = await careerService.getById(id);
      const data = response.data;
      setValues({
        title:        data.title        || '',
        department:   data.department   || '',
        location:     data.location     || '',
        type:         data.type         || 'full-time',
        experience:   data.experience   || '',
        description:  data.description  || '',
        requirements: data.requirements || '',
        status:       data.status       || 'open'
      });
    } catch (error) {
      toast.error('Failed to fetch career opportunity');
      navigate('/careers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, setValues]);

  useEffect(() => {
    if (isEditing && id) {
      fetchCareer();
    }
  }, [isEditing, id, fetchCareer]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please fix the form errors');
      return;
    }

    setSaving(true);

    try {
      if (isEditing) {
        await careerService.update(id, values);
        toast.success('Career opportunity updated successfully');
      } else {
        await careerService.create(values);
        toast.success('Career opportunity created successfully');
      }
      navigate('/careers');
    } catch (error) {
      toast.error(
        isEditing
          ? 'Failed to update career opportunity'
          : 'Failed to create career opportunity'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (e) => {
    setFieldValue('status', e.target.checked ? 'open' : 'closed');
  };

  const fillDemoData = () => {
    setValues({
      title:        'Senior Structural Engineer',
      department:   'Engineering',
      location:     'Mumbai, India',
      type:         'full-time',
      experience:   '3-5 years',
      description:  '<p>We are looking for a <strong>Senior Structural Engineer</strong> to join our growing team. You will be responsible for designing and overseeing structural systems for large-scale infrastructure projects.</p>',
      requirements: '<ul><li>B.E./B.Tech in Civil/Structural Engineering</li><li>3-5 years of hands-on structural design experience</li><li>Proficiency in STAAD.Pro, ETABS, or similar tools</li><li>Strong analytical and problem-solving skills</li></ul>',
      status:       'open',
    });
  };

  if (loading) {
    return (
      <div className="form-loading">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="career-form-page">
      <div className="form-header">
        <Button
          variant="ghost"
          icon={<FiArrowLeft />}
          onClick={() => navigate('/careers')}
        >
          Back to Careers
        </Button>
        <h2>{isEditing ? 'Edit Career Opportunity' : 'Add Career Opportunity'}</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          {/* ── Main Content ── */}
          <div className="form-main">
            <Card title="Position Details">
              <div className="form-section form-input-field">
                <ReviewFormInput
                  name="title"
                  label="Job Title"
                  placeholder="e.g. Senior Structural Engineer"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.title}
                  touched={touched.title}
                  required
                  className="form-text-input"
                />
              </div>

              <div className="form-row">
                <div className="form-input-field">
                  <ReviewFormInput
                    name="department"
                    label="Department"
                    placeholder="e.g. Engineering"
                    value={values.department}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.department}
                    touched={touched.department}
                    required
                    className="form-text-input"
                  />
                </div>
                <div className="form-input-field">
                  <ReviewFormInput
                    name="location"
                    label="Location"
                    placeholder="e.g. Mumbai, India"
                    value={values.location}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.location}
                    touched={touched.location}
                    required
                    className="form-text-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-input-field">
                  <Select
                    name="type"
                    label="Employment Type"
                    value={values.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    options={typeOptions}
                    error={errors.type}
                    touched={touched.type}
                    required
                  />
                </div>
                <div className="form-input-field">
                  <ReviewFormInput
                    name="experience"
                    label="Experience Required"
                    placeholder="e.g. 3-5 years"
                    value={values.experience}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.experience}
                    touched={touched.experience}
                    required
                    className="form-text-input"
                  />
                </div>
              </div>
            </Card>

            <Card title="Job Description">
              <div className="form-section">
                <RichTextEditor
                  label="Description"
                  value={values.description}
                  onChange={(val) => setFieldValue('description', val)}
                  placeholder="Describe the role and responsibilities…"
                />
              </div>
            </Card>

            <Card title="Requirements">
              <div className="form-section">
                <RichTextEditor
                  label="Requirements"
                  value={values.requirements}
                  onChange={(val) => setFieldValue('requirements', val)}
                  placeholder="List qualifications, skills, and experience required…"
                />
              </div>
            </Card>
          </div>

          {/* ── Sidebar ── */}
          <div className="form-sidebar">
            <Card title="Publish">
              <div className="publish-section">
                <Toggle
                  name="status"
                  checked={values.status === 'open'}
                  onChange={handleStatusChange}
                  label={values.status === 'open' ? 'Open for Applications' : 'Closed'}
                />
              </div>
              {!isEditing && (
                <div className="demo-data-section">
                  <Button
                    type="button"
                    variant="outline"
                    icon={<FiZap />}
                    onClick={fillDemoData}
                    fullWidth
                    title="Populate form with sample data for testing"
                  >
                    Fill Demo Data
                  </Button>
                </div>
              )}

              <div className="publish-actions">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/careers')}
                  fullWidth
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  icon={<FiSave />}
                  loading={saving}
                  fullWidth
                >
                  {isEditing ? 'Update' : 'Save'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CareerFormPage;
